// The Rev UID every discipline names a revision by:
//
//   "{PLANT}-{WBS}-{KIND}{REV}"   e.g. CDT-26001-PRO-001-PV008-R0
//
// {PLANT} is the plant/project the WBS sits under and {WBS} the WBS's own
// name - the two identifiers shown as "PROJECT# UID" and "WBS UID" in every
// module header. The kind tells apart the records saved alongside one
// another for the same revision.
//
// Built here so every producer writes the same string: PVContext,
// PipingContext and structureModelingContext when they save a revision, and
// the projects tree when it seeds a brand-new WBS's first revision (see
// seedFirstRevision in useEmsProjectsTreeData).

// Local rather than pvUID's `norm`, so this module - shared by PV, Piping and
// Structural - carries no dependency on PV's own helpers.
const trim = (value) =>
  typeof value === "string" ? value.trim() : String(value ?? "").trim();

// The record a revision IS: PV's Input Data, Piping's and Structural's
// Estimation. Revisions stored before this token changed keep the UID they
// were written with ("...-INP0", "...-EST-R0"); only new saves use this.
export const REVISION_DOCUMENT_KIND = "R";

// PV only: the Estimation and Calculation records saved next to an Input Data
// revision, inside that same revision.
export const ESTIMATION_DOCUMENT_KIND = "EST";
export const CALCULATION_DOCUMENT_KIND = "CALC";

export function plantProjectTag(plantName, projectName) {
  return `${trim(plantName) || "PLANT"}-${trim(projectName) || "PROJECT"}`;
}

export function revisionDocumentUID(
  plantName,
  projectName,
  revision,
  kind = REVISION_DOCUMENT_KIND,
) {
  return `${plantProjectTag(plantName, projectName)}-${kind}${revision}`;
}

// A revision's UID belongs to that revision alone, so branching one into a new
// revision must not carry the parent's UID over - the new revision gets its
// own the moment it is saved. Used next to stripRevisionSaveStamp, which drops
// who saved the parent for the same reason.
export const stripRevisionDocumentUID = (revisionData) => {
  if (!revisionData || typeof revisionData !== "object") return revisionData;

  const { documentUID, ...rest } = revisionData;
  return rest;
};

// Keys a revision entry can carry without holding any of the user's actual
// work: its UID and its save stamp. A revision with nothing but these is an
// empty revision (the tree seeds one on every new WBS - see seedFirstRevision).
export const REVISION_METADATA_KEYS = [
  "documentUID",
  "savedBy",
  "savedById",
  "savedAt",
];

export const isEmptyRevisionEntry = (revisionData) =>
  !revisionData ||
  typeof revisionData !== "object" ||
  Object.keys(revisionData).every((key) =>
    REVISION_METADATA_KEYS.includes(key),
  );
